import type {
  BalanceSourceHealthResponse,
  RevenueByModelDailyResponse,
  RevenueByModelResponse,
  RevenueBySkuSeriesResponse,
  RevenueByTenantResponse,
  RevenueByTenantSort,
  RevenueByUpstreamResponse,
  RevenueByUpstreamSeriesResponse,
  RevenueDailyResponse,
  RevenueSeriesGranularity,
  RevenueSummaryResponse,
  RevenueWindow,
  ThroughputBySkuSeriesResponse,
  ThroughputByUpstreamSeriesResponse,
  ThroughputSeriesGranularity,
} from '@nadoutong/contracts';
import { useQuery } from '@tanstack/react-query';
import type { UseQueryResult } from '@tanstack/react-query';
import { useSession } from '../hooks/useSession.tsx';
import { opsApiRequest } from './client.ts';

/**
 * Typed wrappers + React Query hooks for the `/v1/ops/revenue`
 * surface (Epic #84 P11; by-model daily series + tenant sort added by
 * epic #1643).
 *
 * All endpoints are read-only GET, bearer-authed upstream via the
 * ops-api session cookie. Query-key shape keeps window + filter args
 * as part of the key so switching filters hits a fresh cache slot
 * without manual invalidation.
 *
 *   - summary: `['revenue', 'summary', window, brand, from, to]`
 *   - balance source health: `['revenue', 'balance-source-health']`
 *   - daily:   `['revenue', 'daily', days, brand]`
 *   - by-model: `['revenue', 'by-model', brand, from, to]`
 *   - by-model daily: `['revenue', 'by-model-daily', days, brand, limit]`
 *   - by-tenant: `['revenue', 'by-tenant', limit, brand, sort, from, to]`
 *   - by-sku series: `['revenue', 'by-sku-series', granularity, brand, from, to]`
 *   - by-upstream: `['revenue', 'by-upstream', brand, from, to]`
 *   - by-upstream series: `['revenue', 'by-upstream-series', granularity, brand, from, to]`
 *   - by-sku throughput: `['revenue', 'by-sku-throughput-series', granularity, brand, from, to]`
 *   - by-upstream throughput: `['revenue', 'by-upstream-throughput-series', granularity, brand, from, to]`
 */

const BASE = '/v1/ops/revenue';

export type BrandFilter = '' | 'nadoutong' | 'nxtpath';

/**
 * `from`/`to` are only sent for `window === 'custom'` — named windows
 * are resolved server-side (UTC+8 business timezone, #1001).
 */
export interface RevenueSummaryQuery {
  readonly window: RevenueWindow;
  readonly brand?: BrandFilter;
  readonly from?: string;
  readonly to?: string;
}

/**
 * `/by-model` takes a free-form `[from, to)` pair; omitting both falls
 * back to the server's max-span default window (366 days ending now).
 */
export interface RevenueByModelQuery {
  readonly brand?: BrandFilter;
  readonly from?: string;
  readonly to?: string;
}

/** `/by-model/daily` — server defaults: days 30, limit 8 (top-N by charged). */
export interface RevenueByModelDailyQuery {
  readonly days?: number;
  readonly brand?: BrandFilter;
  readonly limit?: number;
}

/**
 * `/by-tenant` — `sort` picks the truncation lens BEFORE the limit is
 * applied ('profit' default server-side; 'spend' = top consumption).
 */
export interface RevenueByTenantQuery {
  readonly limit: number;
  readonly brand?: BrandFilter;
  readonly sort?: RevenueByTenantSort;
  readonly from?: string;
  readonly to?: string;
}

/**
 * `/by-sku/series` + `/by-upstream/series` (#1675): full-fleet per-group
 * trends. `from`/`to` are free-form ISO bounds (omitted values fall back
 * server-side: `to` → now, `from` → trailing 30d). The server rejects
 * spans over 90 days (`day`) / 48 hours (`hour`) with 400 — the hub
 * clamps custom ranges client-side before requesting (`planGroupSeries`).
 */
export interface RevenueSeriesQuery {
  readonly granularity: RevenueSeriesGranularity;
  readonly brand?: BrandFilter;
  readonly from?: string;
  readonly to?: string;
}

/**
 * `/by-sku/throughput-series` + `/by-upstream/throughput-series`
 * (#1698): per-group RPM/TPM trends. Same free-form bounds semantics as
 * `RevenueSeriesQuery`; the granularity additionally admits `'minute'`
 * (an API capability the hub does not request yet — today windows use
 * `hour`, matching the money series plan).
 */
export interface ThroughputSeriesQuery {
  readonly granularity: ThroughputSeriesGranularity;
  readonly brand?: BrandFilter;
  readonly from?: string;
  readonly to?: string;
}

/**
 * `/by-upstream` window aggregate (#1675): same free-form `[from, to)`
 * surface as `/by-model`. The hub always sends the same (possibly
 * clamped) bounds as the series request so one tab never mixes two
 * different windows.
 */
export interface RevenueByUpstreamQuery {
  readonly brand?: BrandFilter;
  readonly from?: string;
  readonly to?: string;
}

export const REVENUE_QUERY_KEYS = {
  summary: (query: RevenueSummaryQuery) =>
    [
      'revenue',
      'summary',
      query.window,
      query.brand ?? '',
      query.window === 'custom' ? query.from ?? '' : '',
      query.window === 'custom' ? query.to ?? '' : '',
    ] as const,
  balanceSourceHealth: () => ['revenue', 'balance-source-health'] as const,
  daily: (days: number, brand: BrandFilter) => ['revenue', 'daily', days, brand] as const,
  byModel: (query: RevenueByModelQuery) =>
    ['revenue', 'by-model', query.brand ?? '', query.from ?? '', query.to ?? ''] as const,
  byModelDaily: (query: RevenueByModelDailyQuery) =>
    ['revenue', 'by-model-daily', query.days ?? 0, query.brand ?? '', query.limit ?? 0] as const,
  byTenant: (query: RevenueByTenantQuery) =>
    [
      'revenue',
      'by-tenant',
      query.limit,
      query.brand ?? '',
      query.sort ?? '',
      query.from ?? '',
      query.to ?? '',
    ] as const,
  bySkuSeries: (query: RevenueSeriesQuery) =>
    [
      'revenue',
      'by-sku-series',
      query.granularity,
      query.brand ?? '',
      query.from ?? '',
      query.to ?? '',
    ] as const,
  byUpstream: (query: RevenueByUpstreamQuery) =>
    ['revenue', 'by-upstream', query.brand ?? '', query.from ?? '', query.to ?? ''] as const,
  byUpstreamSeries: (query: RevenueSeriesQuery) =>
    [
      'revenue',
      'by-upstream-series',
      query.granularity,
      query.brand ?? '',
      query.from ?? '',
      query.to ?? '',
    ] as const,
  bySkuThroughputSeries: (query: ThroughputSeriesQuery) =>
    [
      'revenue',
      'by-sku-throughput-series',
      query.granularity,
      query.brand ?? '',
      query.from ?? '',
      query.to ?? '',
    ] as const,
  byUpstreamThroughputSeries: (query: ThroughputSeriesQuery) =>
    [
      'revenue',
      'by-upstream-throughput-series',
      query.granularity,
      query.brand ?? '',
      query.from ?? '',
      query.to ?? '',
    ] as const,
};

function setOptional(qs: URLSearchParams, key: string, value: string | undefined): void {
  if (value !== undefined && value.length > 0) {
    qs.set(key, value);
  }
}

export async function getRevenueSummary(
  query: RevenueSummaryQuery,
  fetchImpl: typeof fetch,
  signal?: AbortSignal,
): Promise<RevenueSummaryResponse> {
  const qs = new URLSearchParams({ window: query.window });
  setOptional(qs, 'brand', query.brand);
  if (query.window === 'custom') {
    setOptional(qs, 'from', query.from);
    setOptional(qs, 'to', query.to);
  }
  return opsApiRequest<RevenueSummaryResponse>(
    `${BASE}/summary?${qs.toString()}`,
    { signal },
    fetchImpl,
  );
}

export async function getRevenueDaily(
  days: number,
  brand: BrandFilter,
  fetchImpl: typeof fetch,
  signal?: AbortSignal,
): Promise<RevenueDailyResponse> {
  const qs = new URLSearchParams({ days: String(days) });
  if (brand.length > 0) qs.set('brand', brand);
  return opsApiRequest<RevenueDailyResponse>(
    `${BASE}/daily?${qs.toString()}`,
    { signal },
    fetchImpl,
  );
}

export async function getBalanceSourceHealth(
  fetchImpl: typeof fetch,
  signal?: AbortSignal,
): Promise<BalanceSourceHealthResponse> {
  return opsApiRequest<BalanceSourceHealthResponse>(
    `${BASE}/balance-sources/health`,
    { signal },
    fetchImpl,
  );
}

export async function getRevenueByModel(
  query: RevenueByModelQuery,
  fetchImpl: typeof fetch,
  signal?: AbortSignal,
): Promise<RevenueByModelResponse> {
  const qs = new URLSearchParams();
  setOptional(qs, 'brand', query.brand);
  setOptional(qs, 'from', query.from);
  setOptional(qs, 'to', query.to);
  const suffix = qs.toString().length > 0 ? `?${qs.toString()}` : '';
  return opsApiRequest<RevenueByModelResponse>(`${BASE}/by-model${suffix}`, { signal }, fetchImpl);
}

export async function getRevenueByModelDaily(
  query: RevenueByModelDailyQuery,
  fetchImpl: typeof fetch,
  signal?: AbortSignal,
): Promise<RevenueByModelDailyResponse> {
  const qs = new URLSearchParams();
  if (query.days !== undefined) qs.set('days', String(query.days));
  setOptional(qs, 'brand', query.brand);
  if (query.limit !== undefined) qs.set('limit', String(query.limit));
  const suffix = qs.toString().length > 0 ? `?${qs.toString()}` : '';
  return opsApiRequest<RevenueByModelDailyResponse>(
    `${BASE}/by-model/daily${suffix}`,
    { signal },
    fetchImpl,
  );
}

export async function getRevenueByTenant(
  query: RevenueByTenantQuery,
  fetchImpl: typeof fetch,
  signal?: AbortSignal,
): Promise<RevenueByTenantResponse> {
  const qs = new URLSearchParams({ limit: String(query.limit) });
  setOptional(qs, 'brand', query.brand);
  setOptional(qs, 'sort', query.sort);
  setOptional(qs, 'from', query.from);
  setOptional(qs, 'to', query.to);
  return opsApiRequest<RevenueByTenantResponse>(
    `${BASE}/by-tenant?${qs.toString()}`,
    { signal },
    fetchImpl,
  );
}

export function useRevenueSummary(query: RevenueSummaryQuery): UseQueryResult<RevenueSummaryResponse> {
  const { fetchImpl } = useSession();
  return useQuery({
    queryKey: REVENUE_QUERY_KEYS.summary(query),
    queryFn: ({ signal }) => getRevenueSummary(query, fetchImpl, signal),
  });
}

export function useRevenueDaily(days: number, brand: BrandFilter = ''): UseQueryResult<RevenueDailyResponse> {
  const { fetchImpl } = useSession();
  return useQuery({
    queryKey: REVENUE_QUERY_KEYS.daily(days, brand),
    queryFn: ({ signal }) => getRevenueDaily(days, brand, fetchImpl, signal),
  });
}

export function useBalanceSourceHealth(): UseQueryResult<BalanceSourceHealthResponse> {
  const { fetchImpl } = useSession();
  return useQuery({
    queryKey: REVENUE_QUERY_KEYS.balanceSourceHealth(),
    queryFn: ({ signal }) => getBalanceSourceHealth(fetchImpl, signal),
  });
}

export function useRevenueByModel(query: RevenueByModelQuery = {}): UseQueryResult<RevenueByModelResponse> {
  const { fetchImpl } = useSession();
  return useQuery({
    queryKey: REVENUE_QUERY_KEYS.byModel(query),
    queryFn: ({ signal }) => getRevenueByModel(query, fetchImpl, signal),
  });
}

export function useRevenueByModelDaily(
  query: RevenueByModelDailyQuery = {},
): UseQueryResult<RevenueByModelDailyResponse> {
  const { fetchImpl } = useSession();
  return useQuery({
    queryKey: REVENUE_QUERY_KEYS.byModelDaily(query),
    queryFn: ({ signal }) => getRevenueByModelDaily(query, fetchImpl, signal),
  });
}

export function useRevenueByTenant(query: RevenueByTenantQuery): UseQueryResult<RevenueByTenantResponse> {
  const { fetchImpl } = useSession();
  return useQuery({
    queryKey: REVENUE_QUERY_KEYS.byTenant(query),
    queryFn: ({ signal }) => getRevenueByTenant(query, fetchImpl, signal),
  });
}

function seriesQueryString(query: RevenueSeriesQuery | ThroughputSeriesQuery): string {
  const qs = new URLSearchParams({ granularity: query.granularity });
  setOptional(qs, 'brand', query.brand);
  setOptional(qs, 'from', query.from);
  setOptional(qs, 'to', query.to);
  return qs.toString();
}

export async function getRevenueBySkuSeries(
  query: RevenueSeriesQuery,
  fetchImpl: typeof fetch,
  signal?: AbortSignal,
): Promise<RevenueBySkuSeriesResponse> {
  return opsApiRequest<RevenueBySkuSeriesResponse>(
    `${BASE}/by-sku/series?${seriesQueryString(query)}`,
    { signal },
    fetchImpl,
  );
}

export async function getRevenueByUpstreamSeries(
  query: RevenueSeriesQuery,
  fetchImpl: typeof fetch,
  signal?: AbortSignal,
): Promise<RevenueByUpstreamSeriesResponse> {
  return opsApiRequest<RevenueByUpstreamSeriesResponse>(
    `${BASE}/by-upstream/series?${seriesQueryString(query)}`,
    { signal },
    fetchImpl,
  );
}

export async function getThroughputBySkuSeries(
  query: ThroughputSeriesQuery,
  fetchImpl: typeof fetch,
  signal?: AbortSignal,
): Promise<ThroughputBySkuSeriesResponse> {
  return opsApiRequest<ThroughputBySkuSeriesResponse>(
    `${BASE}/by-sku/throughput-series?${seriesQueryString(query)}`,
    { signal },
    fetchImpl,
  );
}

export async function getThroughputByUpstreamSeries(
  query: ThroughputSeriesQuery,
  fetchImpl: typeof fetch,
  signal?: AbortSignal,
): Promise<ThroughputByUpstreamSeriesResponse> {
  return opsApiRequest<ThroughputByUpstreamSeriesResponse>(
    `${BASE}/by-upstream/throughput-series?${seriesQueryString(query)}`,
    { signal },
    fetchImpl,
  );
}

export async function getRevenueByUpstream(
  query: RevenueByUpstreamQuery,
  fetchImpl: typeof fetch,
  signal?: AbortSignal,
): Promise<RevenueByUpstreamResponse> {
  const qs = new URLSearchParams();
  setOptional(qs, 'brand', query.brand);
  setOptional(qs, 'from', query.from);
  setOptional(qs, 'to', query.to);
  const suffix = qs.toString().length > 0 ? `?${qs.toString()}` : '';
  return opsApiRequest<RevenueByUpstreamResponse>(
    `${BASE}/by-upstream${suffix}`,
    { signal },
    fetchImpl,
  );
}

export function useRevenueBySkuSeries(
  query: RevenueSeriesQuery,
): UseQueryResult<RevenueBySkuSeriesResponse> {
  const { fetchImpl } = useSession();
  return useQuery({
    queryKey: REVENUE_QUERY_KEYS.bySkuSeries(query),
    queryFn: ({ signal }) => getRevenueBySkuSeries(query, fetchImpl, signal),
  });
}

export function useRevenueByUpstreamSeries(
  query: RevenueSeriesQuery,
): UseQueryResult<RevenueByUpstreamSeriesResponse> {
  const { fetchImpl } = useSession();
  return useQuery({
    queryKey: REVENUE_QUERY_KEYS.byUpstreamSeries(query),
    queryFn: ({ signal }) => getRevenueByUpstreamSeries(query, fetchImpl, signal),
  });
}

export function useThroughputBySkuSeries(
  query: ThroughputSeriesQuery,
): UseQueryResult<ThroughputBySkuSeriesResponse> {
  const { fetchImpl } = useSession();
  return useQuery({
    queryKey: REVENUE_QUERY_KEYS.bySkuThroughputSeries(query),
    queryFn: ({ signal }) => getThroughputBySkuSeries(query, fetchImpl, signal),
  });
}

export function useThroughputByUpstreamSeries(
  query: ThroughputSeriesQuery,
): UseQueryResult<ThroughputByUpstreamSeriesResponse> {
  const { fetchImpl } = useSession();
  return useQuery({
    queryKey: REVENUE_QUERY_KEYS.byUpstreamThroughputSeries(query),
    queryFn: ({ signal }) => getThroughputByUpstreamSeries(query, fetchImpl, signal),
  });
}

export function useRevenueByUpstream(
  query: RevenueByUpstreamQuery,
): UseQueryResult<RevenueByUpstreamResponse> {
  const { fetchImpl } = useSession();
  return useQuery({
    queryKey: REVENUE_QUERY_KEYS.byUpstream(query),
    queryFn: ({ signal }) => getRevenueByUpstream(query, fetchImpl, signal),
  });
}
